import { useState, useEffect, useRef } from 'react';
import { analyzeAPI, hotfaceAPI, featureFlagsAPI, adminAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Search, 
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Heart,
  MessageSquare,
  Share2,
  ExternalLink,
  Flame,
  CheckCircle2,
  AlertCircle,
  Users,
  Copy,
  CheckCheck,
  Shield,
  Crown,
  Zap,
  Database,
  Eye,
  Target,
  FileCheck,
  Bot,
  ChevronDown,
  ChevronUp,
  Trophy,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import FeatureGuard from '../components/FeatureGuard';

const EngagementBadge = ({ level, size = 'default' }) => {
  const config = {
    high: { label: 'Tương tác Cao', className: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: TrendingUp, glow: 'shadow-emerald-100' },
    medium: { label: 'Trung bình', className: 'bg-amber-50 text-amber-700 border-amber-100', icon: Minus, glow: 'shadow-amber-100' },
    low: { label: 'Tương tác Thấp', className: 'bg-rose-50 text-rose-700 border-rose-100', icon: TrendingDown, glow: 'shadow-rose-100' },
  };
  
  const { label, className, icon: BadgeIcon, glow } = config[level] || config.low;
  
  if (size === 'large') {
    return (
      <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border shadow-sm transition-all duration-300 hover:scale-[1.02] ${className} ${glow}`}>
        <BadgeIcon size={20} className="shrink-0" />
        <span className="font-bold text-sm tracking-tight">{label.toUpperCase()}</span>
      </div>
    );
  }
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border shadow-sm ${className} ${glow}`}>
      <BadgeIcon size={12} />
      {label}
    </span>
  );
};

export default function Analyze() {
  const [urlsInput, setUrlsInput] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savingLink, setSavingLink] = useState(null);
  const [saveTitle, setSaveTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [featureFlag, setFeatureFlag] = useState(null);
  const [forceRefresh, setForceRefreshing] = useState(false);
  const autoAnalyzeRef = useRef(false);

  const { user, isAdmin, refreshUser } = useAuth();
  const t = useTranslation(user);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const prevHistoryRef = useRef([]);

  useEffect(() => {
    fetchHistory();
    
    // Auto-analyze from URL params
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get('url');
    const autoParam = params.get('auto');
    
    if (urlParam && autoParam === 'true' && !autoAnalyzeRef.current) {
      autoAnalyzeRef.current = true;
      const decodedUrl = decodeURIComponent(urlParam);
      setUrlsInput(decodedUrl);
      
      setTimeout(() => {
        triggerAnalysis([decodedUrl]);
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (prevHistoryRef.current.length > 0) {
      history.forEach(newItem => {
        const oldItem = prevHistoryRef.current.find(i => i.id === newItem.id);
        if (oldItem && ['processing', 'pending_approval'].includes(oldItem.status) && ['completed', 'failed'].includes(newItem.status)) {
          if ('Notification' in window && Notification.permission === 'granted') {
            const title = newItem.status === 'completed' ? '✅ Phân tích hoàn tất!' : '❌ Phân tích thất bại';
            const body = newItem.status === 'completed' ? `Đã có kết quả cho link: ${newItem.url}` : `Link: ${newItem.url}\nLý do: ${newItem.description || 'Admin từ chối hoặc lỗi'}`;
            try {
              new Notification(title, { body, icon: '/favicon.ico' });
            } catch (e) { console.error(e); }
          }
        }
      });
    }
    prevHistoryRef.current = history;

    const hasProcessing = history.some(item => item.status === 'processing' || item.status === 'pending_approval');
    let interval;

    if (hasProcessing) {
      interval = setInterval(() => {
        fetchHistory(true);
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [history]);

  const triggerAnalysis = async (links) => {
    if (!links || links.length === 0) return;
    setAnalyzing(true);
    
    const analysisPromises = links.map(async (link) => {
      try {
        const response = await analyzeAPI.analyze(link, forceRefresh);
        const newResult = response.data;
        
        setHistory(prev => {
          const exists = prev.find(item => item.id === newResult.id);
          if (exists) return prev;
          return [newResult, ...prev];
        });
        setExpandedIds(prev => new Set(prev).add(newResult.id));
        
        toast.info(`Hệ thống đang bắt đầu phân tích: ${link}`);
        return { link, success: true };
      } catch (error) {
        toast.error(error.response?.data?.detail || `Yêu cầu thất bại: ${link}`);
        return { link, success: false };
      }
    });

    await Promise.all(analysisPromises);
    await refreshUser();
    setAnalyzing(false);
    setUrlsInput('');
  };

  const fetchHistory = async (silent = false) => {
    if (!silent) setLoadingHistory(true);
    try {
      const [historyRes, flagsRes] = await Promise.all([
        analyzeAPI.getHistory(),
        featureFlagsAPI.getAll()
      ]);
      setHistory(historyRes.data);
      if (!silent) {
        const flag = flagsRes.data.find(f => f.id === 'link_analysis');
        setFeatureFlag(flag);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      if (!silent) setLoadingHistory(false);
    }
  };

  const handleAnalyze = async (e) => {
    if (e) e.preventDefault();
    if (!urlsInput.trim()) return;

    let rawLinks = urlsInput.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    
    const normalizedLinks = rawLinks.map(link => {
      let l = link.toLowerCase();
      if (l.endsWith('/')) l = l.slice(0, -1);
      return l;
    });

    const uniqueLinks = [...new Set(normalizedLinks)];
    if (uniqueLinks.length < rawLinks.length) {
      toast.error('Phát hiện link bị trùng lặp.');
      return;
    }

    if (uniqueLinks.length > 5) {
      toast.error('Tối đa 5 link/lần');
      return;
    }

    if ('Notification' in window && Notification.permission === 'default') {
      const wantNotif = window.confirm('Hệ thống có thể mất vài phút để xử lý hoặc chờ duyệt. Bạn có muốn nhận thông báo về thiết bị (máy tính/điện thoại) khi có kết quả không?');
      if (wantNotif) {
        try { await Notification.requestPermission(); } catch (err) {}
      }
    }

    triggerAnalysis(rawLinks);
  };

  const openSaveDialog = (link) => {
    setSavingLink(link);
    setSaveTitle(link.page_name || `Kết quả - ${new Date().toLocaleDateString('vi-VN')}`);
    setSaveDialogOpen(true);
  };

  const handleSaveHotface = async () => {
    if (!savingLink) return;
    setSaving(true);
    try {
      await hotfaceAPI.save(savingLink.id, saveTitle);
      toast.success('Đã lưu!');
      setSaveDialogOpen(false);
    } catch (error) {
      toast.error('Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (linkId, e) => {
    if (e) e.stopPropagation();
    try {
      await analyzeAPI.cancel(linkId);
      toast.success('Đã tạm ngừng phân tích');
      fetchHistory(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Không thể tạm ngừng');
    }
  };
  
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Đã sao chép!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Lỗi khi sao chép');
    }
  };

  return (
    <FeatureGuard feature={featureFlag}>
      <div className="space-y-6" data-testid="analyze-page">
        {/* Analyze Form */}
        <Card className="animate-fadeIn shadow-sm border-[#EBF1F7] rounded-2xl" data-testid="analyze-form-card">
          <CardHeader>
            <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
              <Search className="text-[#1877F2]" />
              Phân tích Link
              {featureFlag?.is_vip && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] uppercase font-bold rounded-full border border-amber-200 flex items-center gap-1">
                  <Crown size={12} />
                  VIP
                </span>
              )}
            </h1>
            <p className="text-[#606770] text-sm">
              Đánh giá tương tác bài viết, Fanpage hoặc Profile bằng trí tuệ nhân tạo
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAnalyze} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="urls" className="text-xs font-bold text-[#606770]">LINK FACEBOOK (TỐI ĐA 5 LINK)</Label>
                <div className="flex flex-col gap-3">
                  <Textarea
                    id="urls"
                    placeholder="https://facebook.com/..."
                    value={urlsInput}
                    onChange={(e) => setUrlsInput(e.target.value)}
                    className="w-full min-h-[120px] rounded-xl border-[#EBF1F7] focus:ring-blue-100"
                    disabled={analyzing}
                  />
                  <Button 
                    type="submit"
                    className="h-11 px-6 bg-[#1877F2] hover:bg-[#166FE5] shadow-lg shadow-blue-100 rounded-xl"
                    disabled={analyzing || !urlsInput.trim()}
                  >
                    {analyzing ? (
                      <><Loader2 size={18} className="mr-2 animate-spin" /> Đang khởi tạo...</>
                    ) : (
                      <><Zap size={18} className="mr-2" /> PHÂN TÍCH NGAY</>
                    )}
                  </Button>
                </div>
                <p className="text-[11px] text-[#A0A0A0] mt-1">
                  Hỗ trợ: facebook.com, fb.com, m.facebook.com, fb.watch
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* History Section */}
        {history.length > 0 ? (
          <div className="space-y-4">
            {history.map((result, idx) => {
              const id = result.id || idx;
              const isExpanded = expandedIds.has(id);

              return (
                <Card key={id} className={`group animate-fadeIn overflow-hidden border border-[#EBF1F7] shadow-lg shadow-blue-50/20 bg-white rounded-[20px] transition-all duration-300 hover:shadow-xl hover:translate-y-[-2px] ${!isExpanded ? 'mb-2' : 'mb-6'}`}>
                  <div className={`h-[3px] w-full ${result.status === 'pending_approval' ? 'bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse' : result.status === 'processing' ? 'bg-amber-400 animate-pulse' : 'bg-gradient-to-r from-[#1877F2] to-[#31A24C]'}`} />
                  
                  <div 
                    className={`p-4 sm:p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50/50 transition-all ${isExpanded ? 'border-b border-gray-50 bg-slate-50/30' : ''}`}
                    onClick={() => toggleExpand(id)}
                  >
                    <div className="flex items-center gap-5 min-w-0 flex-1">
                      <div className={`w-12 h-12 rounded-[16px] flex items-center justify-center shrink-0 shadow-inner transition-transform group-hover:scale-105 ${result.status === 'pending_approval' ? 'bg-purple-50 text-purple-600 shadow-purple-100' : result.status === 'processing' ? 'bg-amber-50 text-amber-500' : (result.status === 'failed' ? 'bg-rose-50 text-rose-500' : 'bg-blue-50 text-[#1877F2]')}`}>
                        {result.status === 'pending_approval' ? <Sparkles size={22} className="animate-pulse" /> : result.status === 'processing' ? <Loader2 size={22} className="animate-spin" /> : (result.status === 'failed' ? <AlertCircle size={24} /> : <CheckCircle2 size={24} />)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-extrabold text-[#1C1E21] text-base sm:text-lg tracking-tight truncate max-w-[200px] sm:max-w-md">
                            {result.status === 'processing' ? 'Đang phân tích chuyên sâu...' : result.status === 'pending_approval' ? 'Đang xử lý AI nâng cao...' : (result.status === 'failed' ? 'Phân tích thất bại' : (result.page_name || 'Hồ sơ Facebook'))}
                          </h4>
                          {!isExpanded && result.status === 'completed' && <EngagementBadge level={result.engagement_level} />}
                        </div>
                        <div className="flex items-center gap-1.5 text-[#606770]">
                          <ExternalLink size={12} className="opacity-40" />
                          <p className="text-xs truncate max-w-[250px] sm:max-w-lg font-medium opacity-80">{result.url}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                       {result.status === 'processing' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => handleCancel(id, e)}
                            className="h-9 px-3 rounded-xl border-rose-100 text-rose-500 hover:bg-rose-50 hover:text-rose-600 gap-1.5 transition-all"
                          >
                            <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                            <span className="text-[10px] font-bold uppercase tracking-tight">Dừng</span>
                          </Button>
                       )}
                       {!isExpanded && result.status === 'completed' && (
                          <div className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl text-white shadow-lg ${result.engagement_level === 'high' ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-emerald-100' : result.engagement_level === 'medium' ? 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-100' : 'bg-gradient-to-br from-rose-500 to-red-600 shadow-rose-100'}`}>
                             <Zap size={14} className="fill-white" />
                             <span className="text-sm font-black tracking-tight">{result.engagement_score}</span>
                          </div>
                       )}
                       <div className="flex items-center gap-1 text-[#1877F2] font-bold text-xs uppercase tracking-widest bg-blue-50/50 px-3 py-2 rounded-xl group-hover:bg-blue-50 transition-colors">
                          {isExpanded ? <><ChevronUp size={16} /> <span className="hidden sm:inline ml-1">THU GỌN</span></> : <><ChevronDown size={16} /> <span className="hidden sm:inline ml-1">CHI TIẾT</span></>}
                       </div>
                    </div>
                  </div>

                   {isExpanded && (
                    <div className="animate-in slide-in-from-top-2 duration-300">
                      {result.status === 'pending_approval' ? (
                         <CardContent className="p-8 sm:p-12 relative overflow-hidden bg-gradient-to-br from-indigo-50/50 via-purple-50/30 to-blue-50/50 border-t border-purple-100/50">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-pulse" />
                            <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-200/40 rounded-full blur-3xl opacity-60 pointer-events-none" />
                            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-200/40 rounded-full blur-3xl opacity-60 pointer-events-none" />
                            
                            <div className="relative z-10 flex flex-col items-center justify-center space-y-5">
                               <div className="relative">
                                  <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping" />
                                  <div className="w-16 h-16 bg-white rounded-full shadow-xl shadow-purple-500/10 flex items-center justify-center relative z-10 border border-purple-100">
                                     <Sparkles size={28} className="text-purple-600 animate-pulse" />
                                  </div>
                               </div>
                               
                               <div className="text-center space-y-2">
                                  <h3 className="text-lg font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                                     Yêu Cầu Đã Được Tiếp Nhận
                                  </h3>
                                  <p className="text-[13px] text-slate-600 font-medium max-w-sm leading-relaxed mx-auto">
                                     Hệ thống AI đang tiến hành phân tích chuyên sâu. Quá trình có thể mất thêm ít phút và cần sự kiểm duyệt từ chuyên gia để đảm bảo chất lượng cao nhất.
                                  </p>
                               </div>
                               
                               <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-100 mt-2">
                                  <Shield size={14} className="text-amber-500" />
                                  <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Trạng thái: Đang chờ duyệt</span>
                               </div>
                               
                               <Button 
                                 variant="ghost" 
                                 onClick={() => handleCancel(id)}
                                 className="mt-6 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-full px-6 font-semibold text-[11px] transition-colors"
                               >
                                 HỦY YÊU CẦU
                               </Button>
                            </div>
                         </CardContent>
                      ) : result.status === 'processing' ? (
                         <CardContent className="p-12 flex flex-col items-center justify-center space-y-6">
                            <Loader2 size={32} className="animate-spin text-[#1877F2]" />
                            <p className="text-sm font-bold text-[#1C1E21]">Vui lòng đợi trong giây lát...</p>
                            <p className="text-[11px] text-[#606770] text-center max-w-xs leading-relaxed">
                               Hệ thống đang quét chuyên sâu, bạn có thể rời khỏi trang này và quay lại kiểm tra kết quả trong Lịch sử sau vài phút.
                            </p>
                            <Button 
                              variant="outline" 
                              onClick={() => handleCancel(id)}
                              className="mt-4 border-rose-100 text-rose-500 hover:bg-rose-50 rounded-xl px-8 font-bold text-xs"
                            >
                              HỦY YÊU CẦU NÀY
                            </Button>
                         </CardContent>
                      ) : result.status === 'failed' ? (
                        <CardContent className="p-8 flex flex-col items-center justify-center space-y-4 bg-rose-50/30">
                           <AlertCircle size={32} className="text-rose-500" />
                           <p className="text-sm font-bold text-rose-700">{result.description || 'Đã có lỗi xảy ra trong quá trình phân tích.'}</p>
                        </CardContent>
                      ) : (
                        <>
                          {result.status === 'completed' && (
                            <div className="p-6 space-y-8">
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-2">
                                    <h2 className="text-xl font-bold text-[#1C1E21]">{result.page_name}</h2>
                                    {result.ai_analyzed && <Badge variant="secondary" className="bg-blue-50 text-[#1877F2] text-[10px] font-bold border-none uppercase tracking-tighter">AI VERIFIED</Badge>}
                                  </div>
                                  <p className="text-sm text-[#606770] italic">"{result.description}"</p>
                                </div>
                                <EngagementBadge level={result.engagement_level} size="large" />
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                {[
                                  { label: 'Người theo dõi', value: result.followers, icon: Users, color: 'text-indigo-500', bg: 'bg-indigo-50' },
                                  { label: 'Yêu thích', value: result.likes, icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50' },
                                  { label: 'Bình luận', value: result.comments, icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50' },
                                  { label: 'Chia sẻ', value: result.shares, icon: Share2, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                                  { label: 'Hot Score', value: result.engagement_score, icon: Zap, color: 'text-amber-500', bg: 'bg-amber-50' }
                                ].map((stat, sIdx) => (
                                  <div key={sIdx} className="p-4 rounded-2xl bg-white border border-[#EBF1F7] shadow-sm">
                                    <div className="flex items-center gap-2 mb-1">
                                      <stat.icon size={14} className={stat.color} />
                                      <span className="text-[10px] font-bold text-[#606770] uppercase">{stat.label}</span>
                                    </div>
                                    <div className="text-xl font-black text-[#1C1E21]">{stat.value?.toLocaleString() || 0}</div>
                                  </div>
                                ))}
                              </div>

                              <div className="grid md:grid-cols-2 gap-6">
                                <div className="p-5 rounded-2xl bg-[#F7F8FA] border border-gray-100 space-y-4">
                                  <h3 className="text-xs font-bold text-[#1C1E21] uppercase tracking-widest flex items-center gap-2">
                                    <Trophy size={14} className="text-amber-500" /> CHỈ SỐ SỨC MẠNH
                                  </h3>
                                  {[
                                    { label: 'Tương tác/Bài', value: result.engagement_per_post },
                                    { label: 'Tần suất đăng', value: result.post_frequency },
                                    { label: 'Chất lượng ND', value: result.content_quality_score }
                                  ].map((score, scIdx) => (
                                    <div key={scIdx}>
                                      <div className="flex justify-between text-[11px] font-bold mb-1.5">
                                        <span className="text-[#606770]">{score.label}</span>
                                        <span className="text-[#1877F2]">{score.value}%</span>
                                      </div>
                                      <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                        <div className="h-full bg-[#1877F2]" style={{ width: `${score.value}%` }} />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                <div className="p-5 rounded-2xl bg-blue-50/50 border border-blue-100/30 space-y-4">
                                  <h3 className="text-xs font-bold text-[#1877F2] uppercase tracking-widest flex items-center gap-2">
                                    <Sparkles size={14} /> AI INSIGHTS
                                  </h3>
                                  <p className="text-xs text-[#4B5E7D] leading-relaxed italic">{result.ai_summary || "Hệ thống đang tổng hợp nhận xét AI..."}</p>
                                </div>
                              </div>

                              <div className="flex items-center justify-between pt-6 border-t border-gray-50">
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.url, `res-${idx}`)} className="h-8 gap-2 text-[#606770]">
                                  {copiedId === `res-${idx}` ? <CheckCheck size={14} className="text-green-500" /> : <Copy size={14} />}
                                  <span className="text-[11px] font-bold">SAO CHÉP LINK</span>
                                </Button>
                                <div className="flex gap-2">
                                  <Button onClick={() => openSaveDialog(result)} className="bg-[#31A24C] hover:bg-[#2B8A3E] h-10 px-6 rounded-xl font-bold shadow-lg shadow-green-50">
                                    <Flame size={16} className="mr-2" /> LƯU & THEO DÕI
                                  </Button>
                                  {isAdmin && (
                                    <Button variant="outline" className="h-10 px-4 rounded-xl border border-amber-200 text-amber-700 font-bold" onClick={() => adminAPI.addFromAutoResult(result.url, 'both')}>
                                      <Database size={16} />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 px-6 bg-white rounded-[32px] border-2 border-dashed border-[#F0F2F5] text-center">
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mb-6">
              <Search size={32} className="text-[#1877F2] opacity-20" />
            </div>
            <h3 className="text-lg font-bold text-[#1C1E21] mb-2">Chưa có dữ liệu phân tích</h3>
            <p className="text-sm text-[#8C939D] max-w-xs mx-auto">Nhập link Facebook ở trên để bắt đầu khám phá chỉ số tương tác.</p>
          </div>
        )}

        {/* Save Dialog */}
        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogContent className="max-w-[425px] rounded-[24px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-[#1C1E21]">Lưu Hotface</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="saveTitle">Tiêu đề cá nhân</Label>
                <Input id="saveTitle" value={saveTitle} onChange={(e) => setSaveTitle(e.target.value)} placeholder="Tên để bạn dễ nhận diện..." className="rounded-xl" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleSaveHotface} className="bg-[#31A24C] hover:bg-[#2B8A3E] w-full h-11 rounded-xl font-bold shadow-lg shadow-green-100" disabled={saving}>
                {saving ? <><Loader2 size={18} className="mr-2 animate-spin" /> Đang lưu...</> : <><Flame size={18} className="mr-2" /> XÁC NHẬN LƯU</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </FeatureGuard>
  );
}
